import React, { useMemo } from "react";
import { Product } from "@app/types/product";
import { ProductCard } from "@app/components/ProductCard";

// Variants whose size is "MTM" (Made-to-Measure) carry a price markup (~10%).
// Exclude them from CLP price calculations so the displayed price reflects standard sizes.
const isMTMVariant = (v: any) => {
  const sizeOption = v.option2 || v.title || "";
  return sizeOption.toString().toUpperCase().trim() === "MTM";
};

export const ResultItem = ({ product, itemRef }: { product: any; itemRef: (node?: Element | null) => void }) => {
  // Convert reactify search product to Storefront API product object
  const productProps: Product = useMemo(
    () => {
      // Variants used for price range calculation — MTM excluded.
      // Fallback to all variants if filtering leaves nothing (defensive — shouldn't happen).
      const pricedVariants = product.variants.filter((v: any) => !isMTMVariant(v));
      const variantsForPricing = pricedVariants.length > 0 ? pricedVariants : product.variants;

      return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      availableForSale: product.variants.some((variant: any) => variant.available),
      tags: product.tags,
      isGiftCard: false,
      compareAtPriceRange: {
        maxVariantPrice: {
          amount: Math.max(...variantsForPricing.map((v: any) => v.compare_at_price || 0)).toString(),
          currencyCode: product.variants[0].presentment_prices[0].price.currency_code,
        },
        minVariantPrice: {
          amount: Math.min(...variantsForPricing.map((v: any) => v.compare_at_price || 0)).toString(),
          currencyCode: product.variants[0].presentment_prices[0].price.currency_code,
        },
      },
      priceRange: {
        maxVariantPrice: {
          amount: Math.max(...variantsForPricing.map((v: any) => v.price)).toString(),
          currencyCode: product.variants[0].presentment_prices[0].price.currency_code,
        },
        minVariantPrice: {
          amount: Math.min(...variantsForPricing.map((v: any) => v.price)).toString(),
          currencyCode: product.variants[0].presentment_prices[0].price.currency_code,
        },
      },
      images: {
        edges: product.images.map((image: any) => ({
          node: {
            url: image.src,
          },
        })),
      },
      variants: {
        edges: product.variants.map((variant: any) => ({
          node: {
            id: variant.storefrontId,
            title: variant.title,
            availableForSale: variant.available,
            quantityAvailable: variant.inventory_quantity,
            price: {
              amount: variant.price.toString(),
              currencyCode: variant.presentment_prices[0].price.currency_code,
            },
            compareAtPrice: variant.compare_at_price
              ? {
                  amount: variant.compare_at_price.toString(),
                  currencyCode: variant.presentment_prices[0].price.currency_code,
                }
              : null,
            ...(variant.option2 ? { selectedOptions: [{ name: "size", value: variant.option2 }] } : {}),
          },
        })),
      },
    };
    },
    [product],
  );

  return <ProductCard product={productProps} itemRef={itemRef} />;
};
